import { Inject, Injectable } from '@angular/core';
import { AppConfig, APP_CONFIG } from '@core/models';
import { CookieDict, CookieOptions, CookieService } from 'ngx-cookie';

export type CookieStorageEntry = 'token' | 'refresh-token';

@Injectable({
  providedIn: 'root',
})
export class CookieStorageService {
  constructor(private readonly cookieService: CookieService, @Inject(APP_CONFIG) private config: AppConfig) {}

  public hasKey = (name: CookieStorageEntry): boolean => this.cookieService.hasKey(name);

  public delete = (name: string, path?: string, domain?: string): void =>
    this.cookieService.remove(name, {
      path,
      domain,
    } as CookieOptions);

  public deleteAll = (): void => this.cookieService.removeAll();

  public getAll = (): CookieDict => this.cookieService.getAll();

  public get = (name: CookieStorageEntry): string | undefined => this.cookieService.get(name);

  public getObject = <T>(name: CookieStorageEntry): T | undefined => {
    try {
      const rawValue = this.cookieService.get(name);

      return rawValue ? (JSON.parse(rawValue) as T) : undefined;
    } catch {
      return undefined;
    }
  };

  public set = (
    name: CookieStorageEntry,
    value: string,
    expires?: string | Date,
    domain?: string,
    secure?: boolean,
    sameSite?: 'Lax' | 'None' | 'Strict',
    path = '/'
  ): void => {
    if (this.config.production && !sameSite) {
      this.cookieService.put(name, value, {
        path: '/',
        domain: domain,
        expires: expires,
        secure: true,
        sameSite: 'none',
      } as CookieOptions);
    } else if (!this.config.production && !sameSite) {
      this.cookieService.put(name, value, {
        path: '/',
        domain: domain,
        secure: secure,
        expires: expires,
        sameSite: 'lax',
      } as CookieOptions);
    } else {
      this.cookieService.put(name, value, {
        expires,
        path,
        domain,
        secure,
        sameSite,
      } as CookieOptions);
    }
  };

  public setObject = <T extends object>(
    name: CookieStorageEntry,
    value: T,
    expires?: number | Date,
    domain?: string,
    secure?: boolean,
    sameSite?: 'Lax' | 'None' | 'Strict',
    path = '/'
  ): void => {
    if (this.config.production && !sameSite) {
      this.cookieService.putObject(name, value, {
        expires: expires,
        path: '/',
        domain: domain,
        secure: true,
        sameSite: 'none',
      } as CookieOptions);
    } else if (!this.config.production && !sameSite) {
      this.cookieService.putObject(name, value, {
        expires: expires,
        path: '/',
        domain: domain,
        secure: secure,
        sameSite: 'lax',
      } as CookieOptions);
    } else {
      this.cookieService.putObject(name, value, {
        expires,
        path,
        domain,
        secure,
        sameSite,
      } as CookieOptions);
    }
  };
}
